import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Baby, Shield, Calendar, DollarSign, MessageSquare, 
  FileText, Users, CheckCircle, Clock, Heart, Star, ArrowRight,
  MessageCircle, Send, X, Sparkles,
  Truck, Newspaper, Smartphone
} from 'lucide-react';
import API from '@/config/api';
import { DemoVideoButton } from '@/components/DemoVideoButton';
import { useAuth } from '@/context/AuthContext';

const ROLE_HOME = { admin: '/admin', provider: '/provider', driver: '/driver', parent: '/parent' };

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (!standalone || loading) return;
    navigate(user ? (ROLE_HOME[user.role] || '/parent') : '/login', { replace: true });
  }, [user, loading, navigate]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: 'Hi! 👋 I\'m here to help you learn about CareKids WI. Ask me anything about our child care management platform!' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API}/ai/landing-chat`, {
        message: userMessage,
        history: chatMessages.slice(-6) // Last 6 messages for context
      });
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again or book a demo to speak with our team directly!' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const features = [
    {
      icon: <Baby className="w-8 h-8" />,
      title: "Child Enrollment",
      description: "Streamlined enrollment process with digital forms and record management"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Attendance Tracking",
      description: "Easy check-in/check-out system with real-time updates for parents"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Staff Management",
      description: "Schedule, certifications, and training tracking all in one place"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "DCF Compliance",
      description: "Built-in Wisconsin DCF compliance tracking and documentation"
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Billing & Payments",
      description: "Automated invoicing with secure online payment processing"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "AI Daily Reports",
      description: "AI-generated activity summaries parents love to read"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Van & Transportation",
      description: "Mobile driver manifest with live route map, ETA texts to parents, auto attendance sync, and DCF trip logs",
      link: "/transportation"
    },
    {
      icon: <Newspaper className="w-8 h-8" />,
      title: "Newsletters & Posters",
      description: "AI-generated monthly newsletters as printable posters and social share cards"
    }
  ];

  const benefits = [
    "Wisconsin DCF compliant for family and group child care",
    "Van transportation with live route map, driver app, and DCF trip logs",
    "AI-generated newsletters, daily reports, and lesson plans",
    "Secure parent portal with real-time check-in and van ETA alerts",
    "Automated compliance tracking and reminders",
    "Easy-to-use mobile-friendly interface"
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img
                src="/icons/icon-192x192.png?v=4"
                alt="CareKids WI"
                className="w-9 h-9 sm:w-11 sm:h-11 object-contain"
                data-testid="landing-nav-logo"
              />
              <span className="font-heading font-bold text-lg sm:text-xl whitespace-nowrap">CareKids WI</span>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-4">
              <Link to="/download" className="hidden md:inline-flex">
                <Button variant="ghost" className="font-semibold" data-testid="download-nav-btn">
                  <Smartphone className="mr-2 w-4 h-4" />
                  Get the App
                </Button>
              </Link>
              <Link to="/transportation" className="hidden md:inline-flex">
                <Button variant="ghost" className="font-semibold" data-testid="transportation-nav-btn">
                  <Truck className="mr-2 w-4 h-4" />
                  For Transportation
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="font-semibold" data-testid="login-nav-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/pricing">
                <Button className="btn-primary" data-testid="get-started-nav-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:hidden flex gap-2 pb-3 -mt-1" data-testid="mobile-nav-links">
            <Link to="/download" className="flex-1">
              <Button variant="outline" size="sm" className="w-full rounded-full font-semibold" data-testid="download-nav-btn-mobile">
                <Smartphone className="mr-1.5 w-4 h-4" /> Get the App
              </Button>
            </Link>
            <Link to="/transportation" className="flex-1">
              <Button variant="outline" size="sm" className="w-full rounded-full font-semibold" data-testid="transportation-nav-btn-mobile">
                <Truck className="mr-1.5 w-4 h-4" /> For Transportation
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full font-semibold text-sm mb-6">
                Wisconsin DCF Compliant
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-foreground leading-tight mb-6">
                Child Care Management Made{' '}
                <span className="text-primary">Simple</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                The all-in-one platform for Wisconsin child care providers. 
                Manage enrollment, attendance, compliance, and parent communication 
                with ease.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary text-lg px-10 py-6"
                  onClick={() => navigate('/pricing')}
                  data-testid="hero-get-started-btn"
                >
                  Sign Up Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <DemoVideoButton className="btn-secondary text-lg px-8 py-6" testId="hero-watch-demo-btn" autoOpen={searchParams.get('demo') === '1'} />
              </div>
            </div>
            
            <div className="relative animate-fade-in">
              <div className="relative rounded-3xl overflow-hidden shadow-float">
                <img 
                  src="https://images.unsplash.com/photo-1715775491904-ef5cdae40f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGRpdmVyc2UlMjBjaGlsZHJlbiUyMHBsYXlpbmclMjBpbmRvb3JzJTIwbW9udGVzc29yaXxlbnwwfHx8fDE3NjkwMTE3MDR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy children playing in daycare"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-float p-6 animate-slide-up stagger-2">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">100%</p>
                    <p className="text-sm text-muted-foreground">DCF Compliant</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4">
              Everything You Need to Run Your Center
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From enrollment to billing, we've got every aspect of child care 
              management covered with Wisconsin regulations in mind.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-clay p-8 hover:border-primary/30 transition-all group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-bold text-xl mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
                {feature.link && (
                  <Link to={feature.link} className="inline-flex items-center gap-1 mt-4 font-semibold text-primary hover:underline" data-testid="feature-transportation-link">
                    See the driver app <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1544772711-57da9c7368fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjaGlsZGNhcmUlMjBwcm92aWRlciUyMHRlYWNoZXIlMjBoZWxwaW5nJTIwY2hpbGR8ZW58MHx8fHwxNzY5MDExNzA1fDA&ixlib=rb-4.1.0&q=85"
                alt="Teacher helping child"
                className="rounded-3xl shadow-float"
              />
            </div>
            
            <div>
              <span className="inline-block px-4 py-2 bg-secondary/20 text-secondary-foreground rounded-full font-semibold text-sm mb-6">
                Why Choose CareKids WI
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-6">
                Built for Wisconsin Child Care Providers
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our platform is specifically designed to meet Wisconsin DCF regulations 
                for both family child care and group child care facilities.
              </p>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Portal Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-2 bg-accent-coral/20 text-foreground rounded-full font-semibold text-sm mb-6">
                Parent Portal
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-6">
                Keep Parents Connected & Happy
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Parents can view their child's daily activities, receive AI-generated 
                reports, make payments, and communicate with staff – all from their phone.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-muted rounded-2xl">
                  <MessageSquare className="w-8 h-8 text-primary mb-2" />
                  <p className="font-semibold">Direct Messaging</p>
                </div>
                <div className="p-4 bg-muted rounded-2xl">
                  <FileText className="w-8 h-8 text-primary mb-2" />
                  <p className="font-semibold">Daily Reports</p>
                </div>
                <div className="p-4 bg-muted rounded-2xl">
                  <DollarSign className="w-8 h-8 text-primary mb-2" />
                  <p className="font-semibold">Online Payments</p>
                </div>
                <div className="p-4 bg-muted rounded-2xl">
                  <Calendar className="w-8 h-8 text-primary mb-2" />
                  <p className="font-semibold">Attendance History</p>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 relative">
              <img 
                src="https://images.unsplash.com/photo-1648171457827-8289db7d0ff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwYXJlbnQlMjBsb29raW5nJTIwYXQlMjBwaG9uZSUyMGhhcHB5fGVufDB8fHx8MTc2OTAxMTcwN3ww&ixlib=rb-4.1.0&q=85"
                alt="Parent checking phone"
                className="rounded-3xl shadow-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-primary rounded-3xl p-12 shadow-float">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary-foreground mb-4">
              Ready to Simplify Your Child Care Center?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              Join Wisconsin child care providers who are saving time and staying compliant with CareKids WI.
            </p>
            <Button 
              className="bg-white text-primary hover:bg-white/90 rounded-full px-10 py-6 text-lg font-bold shadow-lg"
              onClick={() => navigate('/register')}
              data-testid="cta-get-started-btn"
            >
              Sign Up Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                <img
                  src="/icons/icon-192x192.png?v=4"
                  alt="CareKids WI"
                  className="w-10 h-10 object-contain"
                  data-testid="landing-footer-logo"
                />
              </div>
              <span className="font-heading font-bold text-xl">CareKids WI</span>
            </div>
            <p className="text-white/60 text-sm">
              © 2025 CareKids WI. Wisconsin DCF Compliant Child Care Management.
            </p>
          </div>
        </div>
      </footer>

      {/* AI Help Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50" data-testid="ai-chat-widget">
        {/* Chat Window */}
        {chatOpen && (
          <div className="absolute bottom-20 right-0 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-border overflow-hidden animate-slide-up">
            {/* Chat Header */}
            <div className="bg-primary p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">CareKids AI</p>
                  <p className="text-xs text-white/70">Ask me anything</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => setChatOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-muted/30">
              {chatMessages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                      msg.role === 'user' 
                        ? 'bg-primary text-white rounded-br-md' 
                        : 'bg-white border border-border rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-border rounded-2xl rounded-bl-md p-3 shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleChatSubmit} className="p-4 border-t border-border bg-white">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about CareKids WI..."
                  className="flex-1"
                  disabled={chatLoading}
                  data-testid="ai-chat-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  className="btn-primary"
                  disabled={chatLoading || !chatInput.trim()}
                  data-testid="ai-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Chat Toggle Button */}
        <Button
          className={`w-14 h-14 rounded-full shadow-lg ${chatOpen ? 'bg-muted text-foreground hover:bg-muted/80' : 'btn-primary'}`}
          onClick={() => setChatOpen(!chatOpen)}
          data-testid="ai-chat-toggle"
        >
          {chatOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;
